import { type RefObject, useEffect, useRef } from 'react';

import { unionAabb, type Aabb2, ReadableStore } from '@frontend/viewer-core';

import type {
  DrawingModel,
  SelectionManager,
  ViewportManager,
} from '../session/types';
import { bindOverlayFrame } from '../utils/toolUtils';

/** How far below the selection the flyout floats, in CSS pixels. */
const MENU_OFFSET_PX = 12;

/**
 * How far the flyout is kept from the container's edges, in CSS pixels.
 *
 * Matches `DxfDetailsOverlay`'s own `bottom={2} right={2}` — the theme's
 * `space[2]` token is `1rem` — so every panel floating over the canvas keeps
 * the same margin from its edges.
 */
const VIEWPORT_MARGIN_PX = 16;

/**
 * Keeps the role flyout pinned over the current selection as the view moves.
 *
 * **Imperative on purpose.** The anchor has to follow every pan and zoom frame, and
 * routing that through React state would re-render the menu — and with it the whole
 * mapping chrome — at frame rate, which is exactly what the session's live tier
 * exists to avoid. So this subscribes to the viewport and writes `transform`
 * directly, the same pattern `useDxfMeasureOverlay` uses. The menu's *contents* —
 * which role is active, what is disabled — stay on the reactive tier where they
 * belong, because they only change when a person acts.
 *
 * Positioned over the selection's world bounds rather than the pointer, so the menu
 * stays put once it appears instead of chasing the cursor away from the geometry
 * the user is about to act on. Clamped to the container afterward, so a selection
 * near an edge never floats the menu partly off-canvas.
 */
export const useDxfMappingMenuAnchor = ({
  viewport,
  selection: selectionManager,
  model: modelManager,
  isOpen,
}: {
  viewport: ViewportManager;
  selection: SelectionManager;
  model: ReadableStore<DrawingModel | null>;
  isOpen: boolean;
}): RefObject<HTMLDivElement> => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const menu = menuRef.current;
    if (!menu || !isOpen) {
      return undefined;
    }

    const place = () => {
      const model = modelManager.getState();
      const selection = selectionManager.getState();
      if (!model || selection.size === 0) {
        return;
      }

      // World bounds of everything picked. Loop bounds are already derived, so this
      // is a union over the selection rather than a walk of its points.
      let bounds: Aabb2 | null = null;
      selection.forEach((ref) => {
        const loop = model.loopsById.get(ref.loopId);
        if (!loop) {
          return;
        }
        const chunk = ref.kind === 'edge' ? loop.chunks[ref.chunkIndex] : null;
        bounds = unionAabb(bounds, chunk ? chunk.bounds : loop.bounds);
      });
      if (!bounds) {
        return;
      }

      const [minX, minY, maxX, maxY] = bounds as Aabb2;
      // Both bottom corners, because a quarter-turn of the view swaps which one
      // is visually lowermost — screen Y is what decides where "below" is.
      const [leftX, leftY] = viewport.worldToScreen([minX, minY]);
      const [rightX, rightY] = viewport.worldToScreen([maxX, maxY]);

      const centerX = (leftX + rightX) / 2;
      const bottomY = Math.max(leftY, rightY);

      // Measured rather than assumed: the menu's own box (buttons, padding,
      // border) is what has to fit inside the container, and its size can
      // change with the role set without this hook knowing why.
      const { width: menuWidth, height: menuHeight } =
        menu.getBoundingClientRect();
      const { width: viewWidth, height: viewHeight } = viewport.size.getState();

      const clamp = (value: number, min: number, max: number) =>
        Math.min(Math.max(value, min), Math.max(min, max));

      const left = clamp(
        centerX - menuWidth / 2,
        VIEWPORT_MARGIN_PX,
        viewWidth - VIEWPORT_MARGIN_PX - menuWidth,
      );
      const top = clamp(
        bottomY + MENU_OFFSET_PX,
        VIEWPORT_MARGIN_PX,
        viewHeight - VIEWPORT_MARGIN_PX - menuHeight,
      );

      menu.style.transform = `translate(${left}px, ${top}px)`;
      // Revealed only once placed. The menu mounts hidden, so it never flashes at
      // the container's top-left on the frame before the first placement lands.
      menu.style.visibility = 'visible';
    };

    return bindOverlayFrame({
      draw: place,
      subscriptions: [
        viewport.subscribe,
        viewport.size.subscribe,
        selectionManager.subscribe,
        modelManager.subscribe,
      ],
    });
  }, [viewport, selectionManager, modelManager, isOpen]);

  return menuRef;
};
